import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "providers/auth";
import useQueryParams from "components/commons/hooks/useQueryParams";

import BiometricsDeviceConnectionModal from "apps/user/learner/pages/settings/components/biometrics-device-connection-modal";

const BiometricsModalContext = createContext(null);

const BIOMETRICS_MODAL_DELAY = 10_000;
const BIOMETRICS_MODAL_PATHS = [
  "/user/home",
  "/user/woneindex",
  "/user/discover",
];

interface BiometricsModalLocationState {
  suppressBiometricsModal?: boolean;
  [key: string]: unknown;
}

interface BiometricsModalProviderProps {
  children: React.ReactNode;
}

const BiometricsModalProvider = ({
  children,
}: BiometricsModalProviderProps) => {
  const params = useQueryParams();
  const location = useLocation();
  const navigate = useNavigate();
  const shouldShowWebModalViaQueryParameter =
    params.get("popup") === "connect-biometrics";
  const [isOpen, setIsOpen] = useState(false);
  const { learner, onDismissBiometricsWebModal } = useAuth();
  const shouldShowWebModal = learner.biometrics?.shouldShowWebModal;
  // Boolean, not the object: `learner` churns identity on every profile
  // refetch and would re-run the effect for no reason.
  const canShowBiometricsModal = Boolean(learner.biometrics);
  const locationRef = useRef(location);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousPathnameRef = useRef<string | null>(null);
  const handledPathnameRef = useRef<string | null>(null);
  // Dismissing fires a mutation, so `shouldShowWebModal` only flips once it
  // resolves. Without this the modal reopens on any navigation the user makes
  // inside that window.
  const hasAutoOpenedRef = useRef(false);
  locationRef.current = location;

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const isPathnameNavigation =
      previousPathnameRef.current !== null &&
      previousPathnameRef.current !== location.pathname;
    previousPathnameRef.current = location.pathname;

    if (isPathnameNavigation) {
      handledPathnameRef.current = null;
    }

    const currentLocation = locationRef.current;
    const locationState =
      currentLocation.state as BiometricsModalLocationState | null;
    if (locationState?.suppressBiometricsModal) {
      const remainingState = { ...locationState };
      delete remainingState.suppressBiometricsModal;
      handledPathnameRef.current = location.pathname;
      navigate(
        {
          pathname: location.pathname,
          search: currentLocation.search,
          hash: currentLocation.hash,
        },
        {
          replace: true,
          state: Object.keys(remainingState).length ? remainingState : null,
        }
      );
    }

    if (shouldShowWebModalViaQueryParameter) {
      // A shared link must not open a modal whose QR is dead for companies
      // with biometrics turned off.
      if (!canShowBiometricsModal) {
        return;
      }
      handledPathnameRef.current = location.pathname;
      setIsOpen(true);
      return;
    }

    if (!shouldShowWebModal) {
      return;
    }

    const isTargetPath = BIOMETRICS_MODAL_PATHS.includes(location.pathname);
    if (!isTargetPath || handledPathnameRef.current === location.pathname) {
      return;
    }

    if (hasAutoOpenedRef.current) {
      return;
    }

    if (isPathnameNavigation) {
      handledPathnameRef.current = location.pathname;
      hasAutoOpenedRef.current = true;
      setIsOpen(true);
      return;
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      handledPathnameRef.current = location.pathname;
      hasAutoOpenedRef.current = true;
      setIsOpen(true);
    }, BIOMETRICS_MODAL_DELAY);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [
    canShowBiometricsModal,
    location.pathname,
    navigate,
    shouldShowWebModal,
    shouldShowWebModalViaQueryParameter,
  ]);

  const handleOpen = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    handledPathnameRef.current = location.pathname;
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);

    if (learner.biometrics?.shouldShowWebModal) {
      onDismissBiometricsWebModal();
    }

    params.delete("popup");
    window.history.pushState("", "", window.location.pathname);
  };

  return (
    <BiometricsModalContext.Provider value={{ isOpen, onOpen: handleOpen }}>
      {children}
      <BiometricsDeviceConnectionModal isOpen={isOpen} onClose={handleClose} />
    </BiometricsModalContext.Provider>
  );
};

export const useBiometricsModal = () => useContext(BiometricsModalContext);
export default BiometricsModalProvider;
